import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity'
import { GetObjectCommand, S3Client } from '@aws-sdk/client-s3'
import { getSignedUrl } from '@aws-sdk/s3-request-presigner'

export function selectViewerLocation(config, request, groups) {
  if (!config?.region || !config?.providerName || !Array.isArray(config.locations)) {
    throw new Error('PDF viewing has not been configured. Please contact your administrator.')
  }
  const memberships = Array.isArray(groups) ? groups : []
  const locations = config.locations.filter(location => {
    const prefix = location.prefix && !location.prefix.endsWith('/') ? `${location.prefix}/` : location.prefix || ''
    return location.bucket === request.bucket && request.key.startsWith(prefix) &&
      (location.allAuthenticatedUsers === true ||
        (Array.isArray(location.groups) && location.groups.some(group => memberships.includes(group))))
  }).sort((a, b) => b.prefix.length - a.prefix.length)
  if (!locations.length) throw new Error('Your Okta groups do not grant access to this report location.')
  return locations[0]
}

function checkAborted(signal) {
  if (signal?.aborted) throw new DOMException('Report request cancelled.', 'AbortError')
}

export async function getViewerLinks(request, idToken, signal) {
  let config
  try {
    config = JSON.parse(process.env.REACT_APP_VIEWER_CONFIG || 'null')
  } catch (_) {
    throw new Error('PDF viewing has not been configured. Please contact your administrator.')
  }
  const location = selectViewerLocation(config, request, idToken?.claims?.groups)
  if (!idToken?.idToken) throw new Error('Your session has expired. Please reopen this report link.')
  checkAborted(signal)

  // Use a fresh provider and the current token on every load/reload. Cognito
  // validates the signed token and enforces the location's role mapping in AWS.
  const provider = fromCognitoIdentityPool({
    identityPoolId: location.identityPoolId,
    logins: { [config.providerName]: idToken.idToken },
    clientConfig: { region: config.region }
  })
  let credentials
  try {
    credentials = await provider()
  } catch (_) {
    throw new Error('AWS could not authorize this report location. Please check your group access or contact your administrator.')
  }
  checkAborted(signal)
  const remaining = credentials.expiration ? Math.floor((credentials.expiration.getTime() - Date.now()) / 1000) : 120
  const expiresIn = Math.min(120, remaining)
  if (expiresIn < 10) throw new Error('Your AWS session has expired. Please reload the report.')

  const client = new S3Client({ region: config.region, credentials })
  const filename = request.key.split('/').pop()
  // RFC 5987 escapes the filename without allowing it to introduce headers.
  const encodedName = encodeURIComponent(filename).replace(/['()*]/g, char =>
    `%${char.charCodeAt(0).toString(16).toUpperCase()}`)
  const sign = disposition => getSignedUrl(client, new GetObjectCommand({
    Bucket: request.bucket,
    Key: request.key,
    ResponseContentType: 'application/pdf',
    ResponseCacheControl: 'private, no-store',
    ResponseContentDisposition: `${disposition}; filename="report.pdf"; filename*=UTF-8''${encodedName}`
  }), { expiresIn })
  try {
    const [viewUrl, downloadUrl] = await Promise.all([sign('inline'), sign('attachment')])
    checkAborted(signal)
    return { viewUrl, downloadUrl, expiresIn }
  } finally {
    client.destroy()
  }
}
